import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactCopy } from './contactCopy';
import './ContactUs.css';

const EMAIL = 'info@thas-multitech.com';
const emptyFields = (service) => ({ name: '', email: '', service, message: '', website: '' });

const ContactUs = ({ border, defaultService = 'general' }) => {
  const { i18n } = useTranslation();
  const copy = contactCopy[i18n.resolvedLanguage === 'swe' || i18n.language?.startsWith('sv') ? 'swe' : 'en'];
  const formId = useId();
  const [fields, setFields] = useState(() => emptyFields(defaultService));
  const [deliveryAvailable, setDeliveryAvailable] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [errors, setErrors] = useState({});
  const sending = useRef(false);
  const request = useRef(null);
  const submissionId = useRef(null);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    fetch('/api/contact', { signal: controller.signal, headers: { Accept: 'application/json' } })
      .then(async (response) => {
        const data = await response.json();
        if (active) setDeliveryAvailable(response.ok && data.available === true);
      })
      .catch(() => { if (active) setDeliveryAvailable(false); })
      .finally(() => clearTimeout(timer));
    return () => { active = false; controller.abort(); request.current?.abort(); clearTimeout(timer); };
  }, []);

  const update = (event) => {
    const { name, value } = event.target;
    submissionId.current = null;
    setFields((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    if (status === 'sent' || status === 'emailDraft') setStatus('');
  };

  const draftUrl = () => {
    const service = copy.services[fields.service] || copy.services.general;
    const subject = `${copy.subject}: ${service}`;
    const body = `${copy.name}: ${fields.name.trim()}\n${copy.email}: ${fields.email.trim()}\n${copy.service}: ${service}\n\n${fields.message.trim()}`;
    return `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (sending.current) return;
    const nextErrors = {};
    if (!fields.name.trim() || fields.name.trim().length > 100) nextErrors.name = copy.nameError;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim()) || fields.email.length > 254) nextErrors.email = copy.emailError;
    if (fields.message.trim().length < 10 || fields.message.trim().length > 5000) nextErrors.message = copy.messageError;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      event.currentTarget.elements[Object.keys(nextErrors)[0]]?.focus();
      return;
    }
    // A filled honeypot never triggers delivery or a false success message.
    if (fields.website) { setStatus('error'); return; }
    if (deliveryAvailable !== true) {
      window.location.href = draftUrl();
      setStatus('emailDraft');
      return;
    }
    sending.current = true;
    setIsLoading(true);
    setStatus('');
    request.current = new AbortController();
    const timer = setTimeout(() => request.current?.abort(), 20000);
    try {
      // Keep the same ID after an uncertain response so a retry cannot send twice.
      if (!submissionId.current) {
        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        submissionId.current = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      }
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        signal: request.current.signal,
        body: JSON.stringify({ ...fields, submissionId: submissionId.current, name: fields.name.trim(), email: fields.email.trim(), message: fields.message.trim() }),
      });
      const data = await response.json();
      if (!response.ok || data.ok !== true) throw new Error('Delivery not confirmed');
      setStatus('sent');
      submissionId.current = null;
      setFields(emptyFields(fields.service));
    } catch {
      setStatus('error');
    } finally {
      clearTimeout(timer);
      sending.current = false;
      setIsLoading(false);
    }
  };

  return (
    <section className={`contact-container ${border ? 'top-border-radius' : ''}`} aria-labelledby={`${formId}-title`}>
      <div className="contact-intro">
        <span className="contact-eyebrow">{copy.eyebrow}</span>
        <h2 id={`${formId}-title`}>{copy.title}</h2>
        <p className="contact-description">{copy.description}</p>
        <ul className="contact-benefits">
          {copy.benefits.map((benefit) => <li key={benefit}><span aria-hidden="true">✓</span>{benefit}</li>)}
        </ul>
        <div className="contact-direct">
          <span>{copy.direct}</span>
          <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
          <a href="tel:+46760211685">+46 76 021 16 85</a>
          <p>{copy.location}</p>
        </div>
      </div>
      <div className="contact-form-card">
        <h3>{copy.formTitle}</h3>
        <p className="contact-required">{copy.required}</p>
        {deliveryAvailable === false && <p className="contact-notice">{copy.emailMode}</p>}
        <form onSubmit={handleSubmit} noValidate aria-busy={isLoading}>
          <fieldset disabled={isLoading}>
            <legend className="contact-visually-hidden">{copy.formTitle}</legend>
            <div className="contact-field-row">
              <div className="contact-field">
                <label htmlFor={`${formId}-name`}>{copy.name} <span aria-hidden="true">*</span></label>
                <input id={`${formId}-name`} name="name" type="text" autoComplete="name" required maxLength={100} value={fields.name} onChange={update} aria-invalid={!!errors.name} aria-describedby={errors.name ? `${formId}-name-error` : undefined} />
                {errors.name && <span id={`${formId}-name-error`} className="contact-field-error">{errors.name}</span>}
              </div>
              <div className="contact-field">
                <label htmlFor={`${formId}-email`}>{copy.email} <span aria-hidden="true">*</span></label>
                <input id={`${formId}-email`} name="email" type="email" inputMode="email" autoComplete="email" required maxLength={254} value={fields.email} onChange={update} aria-invalid={!!errors.email} aria-describedby={errors.email ? `${formId}-email-error` : undefined} />
                {errors.email && <span id={`${formId}-email-error`} className="contact-field-error">{errors.email}</span>}
              </div>
            </div>
            <div className="contact-field">
              <label htmlFor={`${formId}-service`}>{copy.service}</label>
              <select id={`${formId}-service`} name="service" value={fields.service} onChange={update}>
                {Object.entries(copy.services).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="contact-field">
              <label htmlFor={`${formId}-message`}>{copy.message} <span aria-hidden="true">*</span></label>
              <textarea id={`${formId}-message`} name="message" required minLength={10} maxLength={5000} rows={5} value={fields.message} placeholder={fields.service === '3d-printing' ? copy.printPlaceholder : copy.placeholder} onChange={update} aria-invalid={!!errors.message} aria-describedby={`${formId}-message-hint${errors.message ? ` ${formId}-message-error` : ''}`} />
              <span id={`${formId}-message-hint`} className="contact-field-hint">{copy.messageHint}</span>
              {errors.message && <span id={`${formId}-message-error`} className="contact-field-error">{errors.message}</span>}
            </div>
            <div className="contact-trap" aria-hidden="true">
              <label htmlFor={`${formId}-website`}>Website</label>
              <input id={`${formId}-website`} name="website" value={fields.website} onChange={update} tabIndex={-1} autoComplete="off" />
            </div>
            <p className="contact-privacy">{copy.privacy}</p>
            <button className="contact-submit" type="submit" disabled={isLoading || deliveryAvailable === null}>
              {isLoading ? copy.sending : deliveryAvailable === null ? copy.checking : deliveryAvailable ? copy.send : copy.emailButton}<span aria-hidden="true"> ↗</span>
            </button>
          </fieldset>
          <div className={`contact-status ${status ? `contact-status-${status}` : ''}`} role={status === 'error' ? 'alert' : 'status'} aria-live="polite">
            {status && <p>{copy[status]}</p>}
            {(status === 'error' || status === 'emailDraft') && <a href={draftUrl()}>{copy.fallback}</a>}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactUs;
